import * as React from "react";
import { useState } from "react";
import styled, { css } from "styled-components";
import { Form, Formik } from "formik";
import { Trans, useTranslation } from "react-i18next";
import { Button, Col, Modal, Row } from "components/bootstrap";
import FormikInput from "components/common/FormikInput";
import Spinner from "components/common/Spinner";
import { InputDescription, ModalSubmit, IfPermitted } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { useFailureProcessingConfig } from "./api/failureProcessing";
const StyledDefList = styled.dl.attrs({
  className: "deflist"
})(
  ({ theme }) => css`
    &&.deflist {
      dd {
        padding-left: ${theme.spacings.md};
        margin-left: 200px;
      }
    }
  `
);
const LabelSpan = styled.span(
  ({ theme }) => css`
    margin-left: ${theme.spacings.sm};
    font-weight: bold;
  `
);
const PROCESSING_ERROR_FIELD = "gl2_processing_error";
const FailureProcessingConfig = () => {
  const { t } = useTranslation("enterprise_data_tiering");
  const [showModal, setShowModal] = useState(false);
  const { data: failureConfig, updateFailureProcessingConfig } = useFailureProcessingConfig();
  const sendTelemetry = useSendTelemetry("failure-processing");
  const _saveConfig = (values) => {
    updateFailureProcessingConfig(values).then(() => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.FAILURE_PROCESSING_CONFIGURATION_SAVED, {
        app_pathname: "configurations"
      });
      setShowModal(false);
    });
  };
  const _resetConfig = () => {
    setShowModal(false);
  };
  const modalTitle = t("failure_config.modal_title");
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, t("failure_config.title")), /* @__PURE__ */ React.createElement("p", null, t("failure_config.description")), !failureConfig ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledDefList, null, /* @__PURE__ */ React.createElement("dt", null, t("failure_config.summary.log_indexing_failures")), /* @__PURE__ */ React.createElement("dd", null, failureConfig.persistIndexingFailure ? t("failure_config.enabled") : t("failure_config.disabled")), /* @__PURE__ */ React.createElement("dt", null, t("failure_config.summary.log_processing_failures")), /* @__PURE__ */ React.createElement("dd", null, failureConfig.submitProcessingFailure ? t("failure_config.enabled") : t("failure_config.disabled")), /* @__PURE__ */ React.createElement("dt", null, t("failure_config.summary.log_input_failures")), /* @__PURE__ */ React.createElement("dd", null, failureConfig.persistInputFailure ? t("failure_config.enabled") : t("failure_config.disabled")), /* @__PURE__ */ React.createElement("dt", null, t("failure_config.summary.include_failed_messages")), /* @__PURE__ */ React.createElement("dd", null, failureConfig.persistFailedMessage ? t("failure_config.enabled") : t("failure_config.disabled")), /* @__PURE__ */ React.createElement("dt", null, t("failure_config.summary.continue_on_error")), /* @__PURE__ */ React.createElement("dd", null, failureConfig.keepFailedMessageDuplicate ? t("failure_config.enabled") : t("failure_config.disabled"))), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
    Button,
    {
      type: "button",
      bsSize: "xs",
      bsStyle: "info",
      onClick: () => {
        setShowModal(true);
      }
    },
    t("failure_config.edit_configuration")
  ))), /* @__PURE__ */ React.createElement(Modal, { show: showModal, onHide: _resetConfig }, /* @__PURE__ */ React.createElement(Formik, { onSubmit: _saveConfig, initialValues: failureConfig }, ({ isSubmitting, values }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, modalTitle)), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "persistIndexingFailure",
      id: "persistIndexingFailure",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, t("failure_config.form.log_indexing_failures"))
    }
  ), /* @__PURE__ */ React.createElement(InputDescription, { help: t("failure_config.help.indexing") })), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "submitProcessingFailure",
      id: "submitProcessingFailure",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, t("failure_config.form.log_processing_failures"))
    }
  ), /* @__PURE__ */ React.createElement(InputDescription, { help: t("failure_config.help.processing") })), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "persistInputFailure",
      id: "persistInputFailure",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, t("failure_config.form.log_input_failures"))
    }
  ), /* @__PURE__ */ React.createElement(InputDescription, { help: t("failure_config.help.input") })), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "persistFailedMessage",
      id: "persistFailedMessage",
      disabled: !values.submitProcessingFailure && !values.persistIndexingFailure,
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, t("failure_config.form.include_failed_messages"))
    }
  ), /* @__PURE__ */ React.createElement(InputDescription, { help: t("failure_config.help.include_message") })), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "keepFailedMessageDuplicate",
      id: "keepFailedMessageDuplicate",
      disabled: !values.submitProcessingFailure,
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, t("failure_config.form.continue_on_error"))
    }
  ), /* @__PURE__ */ React.createElement(
    InputDescription,
    {
      help: /* @__PURE__ */ React.createElement(
        Trans,
        {
          t,
          i18nKey: "failure_config.help.continue_on_error",
          values: { field: PROCESSING_ERROR_FIELD },
          components: { code: /* @__PURE__ */ React.createElement("code", null) }
        }
      )
    }
  ))))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      onCancel: _resetConfig,
      isSubmitting,
      submitButtonText: t("failure_config.update_configuration"),
      submitLoadingText: t("failure_config.updating_configuration")
    }
  )))))));
};
export default FailureProcessingConfig;
